<template>
    <rc-in-app-notice-dialog
        notice_type="toll-free-registration"
        notice_location="home"
        title="Toll-Free Registration Information"
        max-width="800"
        :loading="mixin_tcr_loading"
        persistent
        :remind_me_later_text="is_touched ? 'Save & Finish Later' : 'Remind Me Later'"
        complete_text="Finish & Submit"
        :can_complete="mixin_is_form_valid"
        @shown="mixinLoadTcrFormData"
        @remind="onFinishLater"
        @complete="onSubmit"
    >
        <div class="px-2 py-4">
            <div class="px-2">
                <p class="mb-3 text-body-2">
                    We're adding a toll-free number as a backup messaging option. Because local (10DLC) and toll-free messaging operate as separate systems, this helps reduce disruption if one pathway experiences deliverability issues. There's no meaningful cost impact — just more flexibility. A toll-free number costs less than $1/month.
                </p>
                <p class="mb-5 text-body-2">
                    Please complete the information below so we can register this for you.
                </p>
            </div>

            <v-expansion-panels
                class="d-flex flex-column gap-1"
                v-model="active_panel"
                accordion
                flat
                @focusin.native.once="is_touched = true"
            >
                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="mixin_org_info_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ mixin_org_info_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Organization Info</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Legal Company Name"
                            type="text"
                            v-model="mixin_tcr_form_data.organization_legal_name"
                            hint="This should match exactly what the IRS has on file for your company's EIN."
                            required
                            @validated="v => mixinOnFieldValidated('organization_legal_name', v)"
                        />

                        <v-checkbox
                            label="My business has a different DBA (Doing Business As)"
                            class="mt-2 mb-0"
                            v-model="mixin_tcr_form_data.has_dba"
                            hide-details
                            @change="mixin_tcr_form_data.has_dba && $nextTick(() => $refs['dba-input']?.focus())"
                        />

                        <transition name="fade">
                            <rc-base-input
                                v-if="mixin_tcr_form_data.has_dba"
                                ref="dba-input"
                                label="DBA"
                                class="mt-4"
                                v-model="mixin_tcr_form_data.dba"
                                hint="DBA or Brand Name, if different from legal name"
                                required
                                @validated="v => mixinOnFieldValidated('dba', v)"
                            />
                        </transition>

                        <rc-select
                            label="Legal Form"
                            class="mt-4"
                            v-model="mixin_tcr_form_data.tcr_legal_form"
                            :items="$rc.enumChoices('tcr_organiztion_legal_form', false)"
                            hint="Choose which best describes your organization. Most RedChirp customers choose 'Private Company'"
                            required
                            @validated="v => mixinOnFieldValidated('tcr_legal_form', v)"
                        />

                        <rc-base-input
                            type="us-or-ca-tax-id"
                            label="Tax Identification Number (EIN)"
                            class="mt-4"
                            v-model="mixin_tcr_form_data.tax_id"
                            required
                            @validated="v => mixinOnFieldValidated('tax_id', v)"
                        >
                            <template #hint>
                                <div class="d-flex align-center">
                                    <span class="mr-2">Your organization's Tax ID</span>
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <v-icon
                                                small
                                                color="grey"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                mdi-help-circle
                                            </v-icon>
                                        </template>
                                        <div>
                                            <p class="mb-0"><strong>US:</strong> Use your 9-digit Employer Identification Number (EIN)</p>
                                            <p class="mb-0"><strong>Canada:</strong> Nine Digit Business Number (BN 9 - not BN15)</p>
                                        </div>
                                    </v-tooltip>
                                </div>
                            </template>
                        </rc-base-input>

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 1"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="mixin_contact_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ mixin_contact_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Business Contact</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <p class="text-body-2 grey--text text--darken-1">The contact information for your organization's primary, individual contact who should be called or emailed if there is any issue with your toll-free registration.</p>

                        <v-row class="mb-0">
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Contact First Name"
                                    v-model="mixin_tcr_form_data.business_contact_first_name"
                                    class="mb-0"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_contact_first_name', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Contact Last Name"
                                    v-model="mixin_tcr_form_data.business_contact_last_name"
                                    class="mb-0"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_contact_last_name', v)"
                                />
                            </v-col>
                        </v-row>

                        <rc-base-input
                            type="tel"
                            label="Contact Phone Number"
                            class="mt-2"
                            v-model="mixin_tcr_form_data.tcr_phone_number"
                            hint="The Campaign Registry requires you to provide an official phone number (not a personal one)."
                            required
                            @validated="v => mixinOnFieldValidated('tcr_phone_number', v)"
                        />

                        <rc-base-input
                            type="official-email"
                            label="Contact Email"
                            class="mt-4"
                            hint="The Campaign Registry requires you to provide an official email address (not a personal one). This email address is used in your default response to the HELP CTIA keyword. For example, john_doe@yourcompany.com instead of john_doe@gmail.com or support@yourcompany.com."
                            v-model="mixin_tcr_form_data.tcr_contact_email"
                            required
                            @validated="v => mixinOnFieldValidated('tcr_contact_email', v)"
                        />

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 2"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="mixin_address_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ mixin_address_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Business Address</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Street Address"
                            v-model="mixin_tcr_form_data.business_address"
                            hint="This should match exactly what's on file with the IRS and/or your state/province's business registration office"
                            required
                            @validated="v => mixinOnFieldValidated('business_address', v)"
                        />

                        <v-row>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="City"
                                    v-model="mixin_tcr_form_data.business_address_city"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_address_city', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    :label="mixin_state_or_province"
                                    v-model="mixin_tcr_form_data.business_address_state"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_address_state', v)"
                                />
                            </v-col>
                        </v-row>

                        <v-row class="mt-0">
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Zip / Postal Code"
                                    v-model="mixin_tcr_form_data.business_address_zip"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_address_zip', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-select
                                    label="Country"
                                    v-model="mixin_tcr_form_data.country"
                                    :items="$rc.enumChoices('tcr_country', false)"
                                    required
                                    @validated="v => mixinOnFieldValidated('country', v)"
                                />
                            </v-col>
                        </v-row>

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 3"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="mixin_website_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ mixin_website_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Website</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Website URL"
                            v-model="mixin_tcr_form_data.website"
                            hint="Your organization's primary website (e.g. for Microsoft, https://www.microsoft.com)"
                            required
                            @validated="v => mixinOnFieldValidated('website', v)"
                            @blur="mixinOnUrlBlur('website')"
                        />

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 4"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="mixin_policies_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ mixin_policies_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Policies</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Privacy Policy URL"
                            v-model="mixin_tcr_form_data.url_privacy_policy"
                            hint="The web address (URL) for your organization's privacy policy (e.g. for Microsoft, https://privacy.microsoft.com/en-us/privacy-statement)"
                            required
                            @validated="v => mixinOnFieldValidated('url_privacy_policy', v)"
                            @blur="mixinOnUrlBlur('url_privacy_policy')"
                        />

                        <rc-select
                            class="mt-4"
                            label="Privacy Policy Addendum"
                            v-model="mixin_tcr_form_data.privacy_policy_addendum"
                            :items="mixin_privacy_policy_addendum_options"
                            required
                            @validated="v => mixinOnFieldValidated('privacy_policy_addendum', v)"
                        />

                        <div class="text-caption mt-1 mb-2 d-flex align-center">
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            :to="{ name: 'default-privacy-policy-addendum', params: { organization_id: mixin_organization_id } }"
                                            text="Privacy policy addendum template"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Our privacy policy addendum template, tailored for your organization</span>
                            </v-tooltip>
                            <span class="mx-2 grey--text">|</span>
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            href="https://redchirp.com/texting-requirements-of-your-privacy-policy/"
                                            text="Carrier requirements"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Examples of acceptable privacy policy language for crafting your own</span>
                            </v-tooltip>
                        </div>

                        <v-divider class="my-4" />

                        <rc-base-input
                            label="Terms and Conditions URL"
                            class="mt-4"
                            v-model="mixin_tcr_form_data.url_terms_and_conditions"
                            hint="The web address (URL) for your organization's terms and conditions (e.g. for Microsoft, https://www.microsoft.com/en-us/legal/terms-of-use)"
                            required
                            @validated="v => mixinOnFieldValidated('url_terms_and_conditions', v)"
                            @blur="mixinOnUrlBlur('url_terms_and_conditions')"
                        />

                        <rc-select
                            class="mt-4"
                            label="Terms and Conditions Addendum"
                            v-model="mixin_tcr_form_data.terms_and_conditions_addendum"
                            :items="mixin_terms_and_conditions_addendum_options"
                            required
                            @validated="v => mixinOnFieldValidated('terms_and_conditions_addendum', v)"
                        />

                        <div class="text-caption mt-1 mb-2 d-flex align-center">
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            :to="{ name: 'default-terms-and-conditions-addendum', params: { organization_id: mixin_organization_id } }"
                                            text="Terms and conditions addendum template"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Our terms and conditions addendum template, tailored for your organization</span>
                            </v-tooltip>
                            <span class="mx-2 grey--text">|</span>
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            href="https://redchirp.com/texting-requirements-and-your-websites-terms-and-conditions/"
                                            text="Carrier requirements"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Examples of acceptable terms and conditions language for crafting your own</span>
                            </v-tooltip>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <div class="mt-4 px-2 text-caption text-center grey--text">
                {{ mixin_sections_complete }} of 5 sections complete
            </div>
        </div>
    </rc-in-app-notice-dialog>
</template>

<script>
import TcrOrganizationSetupMixin from '@/mixins/tcr-organization-setup.js';

export default {
    mixins: [TcrOrganizationSetupMixin],

    data() {
        return {
            active_panel: null,
            is_touched: false,
        };
    },

    computed: {
        mixin_org_info_complete() {
            // overriding mixin computed property to exclude tcr_business_category (only required when in the context of Brand verification, not toll-free)
            if (!this.mixinFieldsValid('organization_legal_name')) return false;
            if (this.mixin_tcr_form_data.has_dba && !this.mixinFieldsValid('dba')) return false;
            if (!this.mixinFieldsValid('tcr_legal_form')) return false;
            if (!this.mixinFieldsValid('tax_id')) return false;
            return true;
        },
    },

    methods: {
        onFinishLater() {
            if (!this.is_touched) {
                this.$rc.snackbar(`We'll remind you once a day until the registration is completed for your organization`);
                return;
            }

            this.mixinSaveTcrFormData({
                success_message: `Progress saved! We'll remind you once a day until the registration is completed for your organization`,
                error_message: 'Unable to save progress. Please try again later.',
            });
        },

        onSubmit() {
            this.mixinSubmitTcrFormData({
                success_message: 'Registration information submitted successfully!',
                error_message: 'Unable to submit registration data. Please try again later.',
                on_error: () => {
                    this.$refs.periodicDialog?.onDismiss();
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
</style>